import usePrivateLink from "../config/usePrivateLink";
import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

import {
  closeModal,
  ToastMain,
  SweetAlertShow,
  LoadingDiv,
} from "../master/Master";

import {
  AssignSyllabusDelete,
  AssignSyllabusStatus,
  FetchTrackingSyllabus,
} from "../../api/SyllabusService";

import { getBatchList } from "../../api/BatchService";
import { getSessions } from "../../api/SessionsService";
import { StaffList } from "../../api/StudentsAttendance";
import { subjectList } from "../../api/Subjects";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import AcademicHeader from "../Academic/AcademicHeader";

const STATUS_META = {
  1: {
    label: "Completed",
    badge: "bg-success",
    icon: "ri-checkbox-circle-line",
  },
  2: {
    label: "Running",
    badge: "bg-warning text-dark",
    icon: "ri-loader-4-line",
  },
  0: { label: "Pending", badge: "bg-danger", icon: "ri-time-line" },
};

// Converts a saved timestamp (e.g. "2024-06-05 10:30:00" or ISO string)
// into the "YYYY-MM-DDTHH:mm" format that <input type="datetime-local" /> needs.
// Returns "" if there's no valid date, so the field just stays empty.
const toDateTimeLocalValue = (value) => {
  if (!value) return "";

  const date = new Date(String(value).replace(" ", "T"));

  if (isNaN(date.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export default function SyllabusAssign() {
  const hasAccess = usePrivateLink();
  // =====================================================
  // STATES
  // =====================================================

  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staffs, setStaffs] = useState([]);
  const [syllabus, setSyllabus] = useState([]);

  const [selectedSession, setSelectedSession] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [status, setStatus] = useState("");

  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [navi, setNavi] = useState({});

  // Status modal
  const [modalData, setModalData] = useState(null);
  const [type, setType] = useState("");

  // Permissions
  const [permissions, setPermissions] = useState([]);

  // =====================================================
  // SESSION LIST
  // =====================================================

  const getSessionList = async () => {
    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionData =
          response?.data?.data?.data || response?.data?.data || [];

        const sessionList = Array.isArray(sessionData) ? sessionData : [];

        setSessions(sessionList);

        // Active session default select
        const activeSession = sessionList.find(
          (session) => String(session?.status) === "1",
        );

        if (activeSession?.id) {
          setSelectedSession(String(activeSession.id));
        }
      } else {
        setSessions([]);
      }
    } catch (error) {
      setSessions([]);

      ToastMain.fire({
        icon: "error",
        title: "Session fetch failed!",
      });
    }
  };

  // =====================================================
  // BATCH LIST
  // SESSION BASED
  // =====================================================

  const batchList = async (sessionId) => {
    if (!sessionId) {
      setBatches([]);
      return;
    }

    try {
      const response = await getBatchList(`&status=1&session_id=${sessionId}`);

      if (response?.data?.status) {
        const batchData = response?.data?.batches || response?.data?.data || [];

        setBatches(Array.isArray(batchData) ? batchData : []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      setBatches([]);

      ToastMain.fire({
        icon: "error",
        title: "Batch fetch failed!",
      });
    }
  };

  // =====================================================
  // SUBJECT LIST
  // DIRECT SUBJECT API
  // =====================================================

  const getSubjects = async () => {
    try {
      const response = await subjectList();

      if (response?.data?.status) {
        const subjectData = response?.data?.data || [];
        setSubjects(Array.isArray(subjectData) ? subjectData : []);
      } else {
        setSubjects([]);
      }
    } catch (error) {
      setSubjects([]);

      ToastMain.fire({
        icon: "error",
        title: "Subject fetch failed!",
      });
    }
  };

  // =====================================================
  // STAFF LIST
  // =====================================================

  const getStaffs = async () => {
    try {
      const response = await StaffList("&type=Teaching");

      if (response?.data?.status) {
        const staffData =
          response?.data?.data?.data || response?.data?.data || [];

        setStaffs(Array.isArray(staffData) ? staffData : []);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);

      ToastMain.fire({
        icon: "error",
        title: "Staff fetch failed!",
      });
    }
  };

  // =====================================================
  // INITIAL API CALLS
  // =====================================================

  useEffect(() => {
    getSessionList();
    getSubjects();
    getStaffs();

    try {
      const userPermissions = localStorage.getItem("user_permissions");

      if (userPermissions) {
        const parsed = JSON.parse(userPermissions);
        setPermissions(parsed?.permissions || []);
      }
    } catch (error) {
      setPermissions([]);
    }
  }, []);

  // =====================================================
  // SESSION CHANGE
  // =====================================================

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;

    setSelectedSession(sessionId);
    setSelectedBatch("");
    setBatches([]);
    setPage(1);

    if (sessionId) {
      batchList(sessionId);
    }
  };

  // =====================================================
  // SESSION CHANGE -> BATCH FETCH
  // =====================================================

  useEffect(() => {
    if (selectedSession) {
      batchList(selectedSession);
    } else {
      setBatches([]);
      setSelectedBatch("");
    }
  }, [selectedSession]);

  // =====================================================
  // TRACKING SYLLABUS LIST
  // =====================================================

  const TrackingsyllabusFetch = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      if (selectedSession) addUrl += `&session_id=${selectedSession}`;
      if (selectedBatch) addUrl += `&batch_id=${selectedBatch}`;
      if (selectedSubject) addUrl += `&subject_id=${selectedSubject}`;
      if (selectedStaff) addUrl += `&teacher_id=${selectedStaff}`;
      if (status !== "") addUrl += `&status=${status}`;

      addUrl += `&page=${page}`;

      const response = await FetchTrackingSyllabus(addUrl);

      if (response?.data?.status) {
        const list = response?.data?.data?.data || [];

        setSyllabus(Array.isArray(list) ? list : []);
        setNavi(response?.data?.data || {});
      } else {
        setSyllabus([]);
        setNavi({});
      }
    } catch (error) {
      setSyllabus([]);
      setNavi({});

      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // FILTER CHANGE -> LIST FETCH
  // =====================================================

  useEffect(() => {
    TrackingsyllabusFetch();
  }, [
    selectedSession,
    selectedBatch,
    selectedSubject,
    selectedStaff,
    status,
    page,
  ]);

  // =====================================================
  // DELETE
  // =====================================================

  const handleDelete = async (id) => {
    setLoading(true);

    try {
      const response = await AssignSyllabusDelete(id);

      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Deleted successfully.",
        );

        await TrackingsyllabusFetch();
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to delete.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // STATUS UPDATE
  // =====================================================

  const StatusUpdate = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const payload = {
        id: formData.get("id"),
        status: formData.get("status"),
        start_date_time: formData.get("start_date_time") || null,
        end_date_time: formData.get("end_date_time") || null,
      };

      const response = await AssignSyllabusStatus(payload);

      if (response?.data?.status) {
        closeModal("statusChange");

        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message || "Status updated successfully.",
        );

        await TrackingsyllabusFetch();

        const closeButton = document.querySelector("#statusChange .btn-close");

        if (closeButton) {
          closeButton.click();
        }

        setModalData(null);
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to update status.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message ??
        "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // MODAL STATUS
  // =====================================================

  useEffect(() => {
    if (modalData) {
      setType(modalData?.status !== undefined ? String(modalData.status) : "");
    } else {
      setType("");
    }
  }, [modalData]);

  // =====================================================
  // RESET
  // =====================================================

  const handleReset = () => {
    const activeSession = sessions.find(
      (session) => String(session?.status) === "1",
    );

    const activeSessionId = activeSession?.id ? String(activeSession.id) : "";

    setSelectedSession(activeSessionId);
    setSelectedBatch("");
    setSelectedSubject("");
    setSelectedStaff("");
    setStatus("");
    setPage(1);

    if (activeSessionId) {
      batchList(activeSessionId);
    }
  };

  // =====================================================
  // PERMISSIONS
  // =====================================================
  const canAddSyllabus = hasAccess("/syllabus/assign/add");

  const hasActiveFilters = Boolean(
    selectedBatch || selectedSubject || selectedStaff || status !== "",
  );

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <>
      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && <LoadingDiv />}

      {/* =====================================================
          LAYOUT
      ===================================================== */}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      {/* =====================================================
                          HEADER
                      ===================================================== */}

                      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-checkbox-multiple-line fs-4" />
                          </div>
                          <div>
                            <h4 className="text-primary m-0">
                              Syllabus Assign
                            </h4>
                            <small className="text-muted">
                              Track syllabus progress by batch and teacher
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          {navi?.total > 0 && (
                            <span className="badge bg-label-secondary rounded-pill px-3 py-2">
                              {navi.total}{" "}
                              {navi.total === 1 ? "record" : "records"}
                            </span>
                          )}

                          {canAddSyllabus && (
                            <Link
                              to="/syllabus/assign/add"
                              className="btn btn-primary btn-sm"
                            >
                              <i className="ri-add-line me-1"></i>
                              Syllabus Assign
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* =====================================================
                          FILTERS
                      ===================================================== */}

                      <div className="card-body pb-2">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="text-uppercase text-muted small fw-semibold">
                            <i className="ri-filter-3-line me-1" />
                            Filters
                          </span>

                          {hasActiveFilters && (
                            <button
                              type="button"
                              className="btn btn-sm btn-text-secondary text-decoration-underline"
                              onClick={handleReset}
                            >
                              Reset filters
                            </button>
                          )}
                        </div>

                        <div className="row g-2">
                          {/* SESSION */}
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedSession}
                                onChange={handleSessionChange}
                              >
                                <option value="">All Sessions</option>
                                {sessions?.map((session) => (
                                  <option key={session?.id} value={session?.id}>
                                    {session?.session || "Session"}
                                    {String(session?.status) === "1"
                                      ? " (Active)"
                                      : " (Inactive)"}
                                  </option>
                                ))}
                              </select>
                              <label>Session</label>
                            </div>
                          </div>

                          {/* BATCH */}
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedBatch}
                                onChange={(e) => {
                                  setSelectedBatch(e.target.value);
                                  setPage(1);
                                }}
                                disabled={!selectedSession}
                              >
                                <option value="">All Batches</option>
                                {batches?.map((batch) => (
                                  <option key={batch?.id} value={batch?.id}>
                                    {batch?.title}
                                  </option>
                                ))}
                              </select>
                              <label>Batch</label>
                            </div>
                          </div>

                          {/* SUBJECT */}
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedSubject}
                                onChange={(e) => {
                                  setSelectedSubject(e.target.value);
                                  setPage(1);
                                }}
                              >
                                <option value="">All Subjects</option>
                                {subjects?.map((subject) => (
                                  <option key={subject?.id} value={subject?.id}>
                                    {subject?.name}
                                  </option>
                                ))}
                              </select>
                              <label>Subject</label>
                            </div>
                          </div>

                          {/* STAFF */}
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedStaff}
                                onChange={(e) => {
                                  setSelectedStaff(e.target.value);
                                  setPage(1);
                                }}
                              >
                                <option value="">All Staff</option>
                                {staffs?.map((staff) => (
                                  <option key={staff?.id} value={staff?.id}>
                                    {staff?.name}
                                  </option>
                                ))}
                              </select>
                              <label>Staff</label>
                            </div>
                          </div>

                          {/* STATUS */}
                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={status}
                                onChange={(e) => {
                                  setStatus(e.target.value);
                                  setPage(1);
                                }}
                              >
                                <option value="">All Status</option>
                                <option value="0">Pending</option>
                                <option value="2">Running</option>
                                <option value="1">Completed</option>
                              </select>
                              <label>Status</label>
                            </div>
                          </div>

                          {/* RESET */}
                          <div className="col-md-2 d-flex align-items-center">
                            <button
                              type="button"
                              className="btn btn-outline-secondary w-100"
                              onClick={handleReset}
                            >
                              <i className="ri-refresh-line me-1" />
                              Reset
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* =====================================================
                          TABLE
                      ===================================================== */}

                      <div className="card-body pt-2">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: 48 }}>#</th>
                                <th>Batch (Course)</th>
                                <th>Subject</th>
                                <th>Teacher</th>
                                <th>Topic</th>
                                <th>Duration</th>
                                <th>Status</th>
                                <th className="text-end pe-4">Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {syllabus.length === 0 ? (
                                <tr>
                                  <td colSpan="8" className="border-0">
                                    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                                      <div
                                        className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center mb-3"
                                        style={{ width: 64, height: 64 }}
                                      >
                                        <i className="ri-checkbox-multiple-line fs-2 text-muted" />
                                      </div>
                                      <h6 className="mb-1">
                                        No syllabus tracker available
                                      </h6>
                                      <p
                                        className="text-muted small mb-3"
                                        style={{ maxWidth: 340 }}
                                      >
                                        {hasActiveFilters
                                          ? "No entries match your current filters. Try resetting them."
                                          : "Assign a syllabus to a batch to start tracking progress."}
                                      </p>
                                      {hasActiveFilters ? (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-secondary"
                                          onClick={handleReset}
                                        >
                                          Reset filters
                                        </button>
                                      ) : (
                                        canAddSyllabus && (
                                          <Link
                                            to="/syllabus/assign/add"
                                            className="btn btn-sm btn-primary"
                                          >
                                            <i className="ri-add-line me-1" />
                                            Syllabus Assign
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                syllabus.map((item, index) => {
                                  const meta =
                                    STATUS_META[String(item?.status)] ??
                                    STATUS_META[0];

                                  return (
                                    <tr key={item?.id}>
                                      <td className="text-muted">
                                        {navi?.from
                                          ? navi.from + index
                                          : index + 1}
                                      </td>

                                      <td>
                                        {item?.batch?.title || "N/A"}{" "}
                                        <span className="text-muted">
                                          ({item?.course?.title || "N/A"})
                                        </span>
                                      </td>

                                      <td>{item?.subject?.name || "N/A"}</td>

                                      <td>{item?.teacher?.name || "N/A"}</td>

                                      <td className="fw-semibold text-primary">
                                        {item?.syllabus?.title || "N/A"}
                                      </td>

                                      <td>
                                        <span className="badge bg-label-info">
                                          <i className="ri-time-line me-1" />
                                          {item?.syllabus?.duration_day ||
                                            0}{" "}
                                          days
                                        </span>
                                      </td>

                                      {/* STATUS */}
                                      <td>
                                        {hasAccess("/syllabus/assign/status") && (
                                          <button
                                            type="button"
                                            className={`btn btn-sm ${meta.badge} d-inline-flex align-items-center gap-1`}
                                            data-bs-toggle="modal"
                                            data-bs-target="#statusChange"
                                            onClick={() => setModalData(item)}
                                            title="Change status"
                                          >
                                            <i className={meta.icon} />
                                            {meta.label}
                                          </button>
                                        )}
                                      </td>

                                      {/* ACTION */}
                                      <td className="text-end pe-4">
                                        <div className="d-flex justify-content-end gap-2">
                                          {hasAccess("syllabus/assign/update") && (
                                            <Link
                                              to="/syllabus/assign/update"
                                              state={{ syllabusItem: item }}
                                              className="btn btn-sm btn-icon btn-outline-primary"
                                              title="Edit"
                                            >
                                              <i className="ri-pencil-line" />
                                            </Link>
                                          )}

                                          {hasAccess("syllabus/assign/delete") && (
                                            <button
                                              type="button"
                                              className="btn btn-sm btn-icon btn-outline-danger"
                                              title="Delete"
                                              onClick={() => {
                                                Swal.fire({
                                                  title: "Are you sure?",
                                                  text: "This action will permanently delete!",
                                                  icon: "warning",
                                                  showCancelButton: true,
                                                  confirmButtonColor: "#d33",
                                                  cancelButtonColor: "#6c757d",
                                                  confirmButtonText:
                                                    "Yes, delete it!",
                                                  cancelButtonText: "Cancel",
                                                }).then((result) => {
                                                  if (result.isConfirmed) {
                                                    handleDelete(item?.id);
                                                  }
                                                });
                                              }}
                                            >
                                              <i className="ri-delete-bin-line" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>

                          {/* =====================================================
                              PAGINATION
                          ===================================================== */}

                          {syllabus.length > 0 && (
                            <div className="d-flex flex-wrap justify-content-between align-items-center mt-3 gap-2">
                              <p className="mb-0 text-muted small">
                                Showing <strong>{navi?.from || 0}</strong> to{" "}
                                <strong>{navi?.to || 0}</strong> of{" "}
                                <strong>{navi?.total || 0}</strong> entries
                              </p>

                              <ul className="pagination pagination-sm mb-0">
                                {navi?.links?.map((btn, index) => (
                                  <li
                                    key={index}
                                    className={`page-item ${btn?.active ? "active" : ""
                                      } ${btn?.url ? "" : "disabled"}`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      onClick={() => {
                                        if (!btn?.url) return;

                                        const url = new URL(btn.url);
                                        const nextPage =
                                          url.searchParams.get("page");

                                        if (nextPage) {
                                          setPage(Number(nextPage));
                                        }
                                      }}
                                    >
                                      {btn?.label?.includes("Previous") ? (
                                        <i className="ri-skip-back-mini-line" />
                                      ) : btn?.label?.includes("Next") ? (
                                        <i className="ri-skip-forward-mini-line" />
                                      ) : (
                                        btn?.label
                                      )}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =====================================================
          STATUS MODAL
      ===================================================== */}

      <div
        className="modal fade"
        id="statusChange"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={StatusUpdate} key={modalData?.id || "empty"}>
              {/* MODAL HEADER */}
              <div className="modal-header bg-label-primary py-3">
                <h1 className="modal-title fs-5 d-flex align-items-center gap-2">
                  <i className="ri-refresh-line" />
                  Update Status
                </h1>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  onClick={() => setModalData(null)}
                />
              </div>

              {/* MODAL BODY */}
              <div className="modal-body">
                {modalData && (
                  <div className="alert alert-light border d-flex align-items-center gap-2 mb-3">
                    <i className="ri-book-open-line text-primary" />
                    <div className="small">
                      <div className="fw-semibold">
                        {modalData?.syllabus?.title || "Syllabus"}
                      </div>
                      <div className="text-muted">
                        {modalData?.batch?.title || "N/A"} ·{" "}
                        {modalData?.subject?.name || "N/A"}
                      </div>
                    </div>
                  </div>
                )}

                <input type="hidden" name="id" value={modalData?.id || ""} />

                {/* STATUS */}
                <div className="mb-2 form-floating form-floating-outline">
                  <select
                    name="status"
                    className="form-select"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="0">Pending</option>
                    <option value="2">Running</option>
                    <option value="1">Completed</option>
                  </select>
                  <label>Status</label>
                </div>

                {/* RUNNING */}
                {type === "2" && (
                  <div className="form-floating form-floating-outline mb-2">
                    <input
                      className="form-control"
                      type="datetime-local"
                      name="start_date_time"
                      defaultValue={toDateTimeLocalValue(
                        modalData?.start_date_time,
                      )}
                      required
                    />
                    <label>Start Date</label>
                  </div>
                )}

                {/* COMPLETED */}
                {type === "1" && (
                  <>
                    {String(modalData?.status) === "0" && (
                      <div className="form-floating form-floating-outline mb-2">
                        <input
                          className="form-control"
                          type="datetime-local"
                          name="start_date_time"
                          defaultValue={toDateTimeLocalValue(
                            modalData?.start_date_time,
                          )}
                          required
                        />
                        <label>Start Date</label>
                      </div>
                    )}

                    <div className="form-floating form-floating-outline mb-2">
                      <input
                        className="form-control"
                        type="datetime-local"
                        name="end_date_time"
                        defaultValue={toDateTimeLocalValue(
                          modalData?.end_date_time,
                        )}
                        required
                      />
                      <label>End Date</label>
                    </div>
                  </>
                )}
              </div>

              {/* MODAL FOOTER */}
              <div className="modal-footer bg-label-primary py-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  data-bs-dismiss="modal"
                  onClick={() => setModalData(null)}
                >
                  Close
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
